import { useCallback, useMemo, useState } from "react";
import { PickerProject } from "./AddToProjectPicker.types";

type PickerScope = "my" | "shared";
type PickerMode = "overview" | "search" | "drilldown";

export const RECENT_LIMIT = 2;
export const OVERVIEW_LIMIT = 5;

export const filterProjectsByName = (
  projects: PickerProject[],
  query: string,
): PickerProject[] => {
  const normalised = query.trim().toLowerCase();
  if (!normalised) {
    return projects;
  }
  return projects.filter((project) =>
    project.name.toLowerCase().includes(normalised),
  );
};

/**
 * First letter used as the drilldown divider. Diacritics are folded so accented
 * Latin initials bucket with their base letter (e.g. "Über" -> "U"); any remaining
 * non-A-Z initial (digits, symbols, non-Latin scripts) groups under "#".
 */
export const dividerLetterFor = (name: string): string => {
  const first = name
    .trim()
    .charAt(0)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();
  return /[A-Z]/.test(first) ? first : "#";
};

export interface LetterGroup {
  letter: string;
  projects: PickerProject[];
}

export const groupProjectsByLetter = (
  projects: PickerProject[],
): LetterGroup[] => {
  const byLetter = new Map<string, PickerProject[]>();

  projects.forEach((project) => {
    const letter = dividerLetterFor(project.name);
    const group = byLetter.get(letter);
    if (group) {
      group.push(project);
    } else {
      byLetter.set(letter, [project]);
    }
  });

  return [...byLetter.keys()]
    .sort((a, b) => {
      if (a === "#") return -1;
      if (b === "#") return 1;
      return a.localeCompare(b);
    })
    .map((letter) => ({
      letter,
      projects: byLetter
        .get(letter)!
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    }));
};

const getMode = (query: string, scope: PickerScope | null): PickerMode => {
  if (query.trim()) {
    return "search";
  }
  if (scope) {
    return "drilldown";
  }
  return "overview";
};

interface UsePickerStateArgs {
  myProjects: PickerProject[];
  sharedProjects: PickerProject[];
}

export const usePickerState = ({
  myProjects,
  sharedProjects,
}: UsePickerStateArgs) => {
  const [queryRaw, setQueryRaw] = useState("");
  const [drilldownScope, setDrilldownScope] = useState<PickerScope | null>(
    null,
  );

  const mode = getMode(queryRaw, drilldownScope);

  const setQuery = useCallback((value: string) => {
    setQueryRaw(value);
    // Typing always leaves a drilldown and returns to the global search view.
    if (value.trim()) {
      setDrilldownScope(null);
    }
  }, []);

  const openDrilldown = useCallback((scope: PickerScope) => {
    setQueryRaw("");
    setDrilldownScope(scope);
  }, []);

  const closeDrilldown = useCallback(() => setDrilldownScope(null), []);

  const reset = useCallback(() => {
    setQueryRaw("");
    setDrilldownScope(null);
  }, []);

  const overview = useMemo(
    () => ({
      recent: myProjects.slice(0, RECENT_LIMIT),
      myTop: myProjects.slice(0, OVERVIEW_LIMIT),
      myCount: myProjects.length,
      hasMoreMy: myProjects.length > OVERVIEW_LIMIT,
      sharedCount: sharedProjects.length,
      hasMoreShared: sharedProjects.length > OVERVIEW_LIMIT,
      sharedTop: sharedProjects.slice(0, OVERVIEW_LIMIT),
    }),
    [myProjects, sharedProjects],
  );

  const search = useMemo(() => {
    const my = filterProjectsByName(myProjects, queryRaw);
    const shared = filterProjectsByName(sharedProjects, queryRaw);
    return { my, shared, isEmpty: my.length === 0 && shared.length === 0 };
  }, [myProjects, sharedProjects, queryRaw]);

  const drilldown = useMemo(() => {
    if (!drilldownScope) {
      return null;
    }
    const source = drilldownScope === "my" ? myProjects : sharedProjects;
    return {
      scope: drilldownScope,
      count: source.length,
      groups: groupProjectsByLetter(source),
    };
  }, [drilldownScope, myProjects, sharedProjects]);

  return {
    mode,
    query: queryRaw,
    setQuery,
    openDrilldown,
    closeDrilldown,
    reset,
    overview,
    search,
    drilldown,
  };
};
